/**
 * Typh Size & Fit — fixed right panel (desktop) / full-screen sheet (mobile).
 * Does not mirror, resize, or touch the live product gallery.
 * Chart size clicks highlight measurements only — they do not change the PDP variant.
 */
if (!customElements.get('typh-size-fit')) {
  class TyphSizeFit extends HTMLElement {
    constructor() {
      super();
      this.unit = 'in';
      this.activeChartId = null;
      this.activeOptionName = null;
      this.highlightSize = null;
      this.lastFocus = null;
      this.savedScrollY = 0;
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      const dataEl = document.getElementById(`TyphSizeFitData-${this.dataset.section}`);
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.shell = this.querySelector('.typh-sf__shell');
      this.panel = this.querySelector('.typh-sf__panel');
      this.fitEl = this.querySelector('[data-typh-sf-fit]');
      this.fitCopyEl = this.querySelector('[data-typh-sf-fit-copy]');
      this.fabricEl = this.querySelector('[data-typh-sf-fabric]');
      this.careEl = this.querySelector('[data-typh-sf-care]');
      this.sizesEl = this.querySelector('[data-typh-sf-sizes]');
      this.mobileRows = this.querySelector('[data-typh-sf-mobile-rows]');
      this.tabsEl = this.querySelector('[data-typh-sf-tabs]');
      this.table = this.querySelector('[data-typh-sf-table]');
      this.statusEl = this.querySelector('[data-typh-sf-status]');
      this.thead = this.table.querySelector('thead');
      this.tbody = this.table.querySelector('tbody');

      this.querySelectorAll('[data-typh-sf-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-typh-sf-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.unit = btn.getAttribute('data-typh-sf-unit');
          this.querySelectorAll('[data-typh-sf-unit]').forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          this.renderMeasurements();
        });
      });

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-typh-sf-open]');
        if (!trigger) return;
        if (trigger.getAttribute('data-typh-sf-section') !== this.dataset.section) return;
        event.preventDefault();
        this.open({
          chartId: trigger.getAttribute('data-typh-sf-chart'),
          optionName: trigger.getAttribute('data-typh-sf-option'),
        });
      });
    }

    get productConfig() {
      return this.data.products[this.data.productHandle];
    }

    get chart() {
      return this.data.charts[this.activeChartId];
    }

    open({ chartId, optionName } = {}) {
      const config = this.productConfig;
      this.activeChartId = chartId || (config && config.defaultChart);
      this.activeOptionName = optionName || (this.chart && this.chart.optionName);
      if (!this.activeChartId || !this.chart) return;

      this.highlightSize = this.readSelectedSize(this.activeOptionName) || this.firstAvailableSize();
      this.lastFocus = document.activeElement;
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      this.hidden = false;
      this.setAttribute('open', '');
      if (this.shell) this.shell.hidden = false;
      this.panel.classList.add('is-open');
      document.documentElement.classList.add('typh-sf-open');
      this.renderTabs();
      this.renderChart();
      document.addEventListener('keydown', this.onKeyDown);
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
    }

    close() {
      if (this.hidden) return;
      this.removeAttribute('open');
      this.panel.classList.remove('is-open');
      if (this.shell) this.shell.hidden = true;
      this.hidden = true;
      document.documentElement.classList.remove('typh-sf-open');
      document.removeEventListener('keydown', this.onKeyDown);
      window.scrollTo(0, this.savedScrollY || 0);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = this.getFocusable();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusable() {
      return Array.from(
        this.panel.querySelectorAll(
          'button:not([disabled]), [href], summary, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    }

    renderTabs() {
      const config = this.productConfig;
      const tabs = config && config.tabs;
      if (!tabs || !tabs.length) {
        this.tabsEl.hidden = true;
        this.tabsEl.innerHTML = '';
        return;
      }
      this.tabsEl.hidden = false;
      this.tabsEl.innerHTML = tabs
        .map((tab) => {
          const active = tab.id === this.activeChartId;
          return `<button type="button" class="typh-sf__tab${active ? ' is-active' : ''}" role="tab" aria-selected="${active}" data-typh-sf-tab="${tab.id}" data-option="${tab.optionName}">${tab.label}</button>`;
        })
        .join('');
      this.tabsEl.querySelectorAll('[data-typh-sf-tab]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeChartId = btn.getAttribute('data-typh-sf-tab');
          this.activeOptionName = btn.getAttribute('data-option');
          this.highlightSize = this.readSelectedSize(this.activeOptionName) || this.firstAvailableSize();
          this.renderTabs();
          this.renderChart();
        });
      });
    }

    renderChart() {
      const chart = this.chart;
      if (!chart) return;
      this.fitEl.textContent = chart.fit;
      if (this.fitCopyEl) this.fitCopyEl.textContent = chart.fit;
      this.fabricEl.textContent = chart.fabric;
      this.careEl.textContent = chart.care;
      this.renderMeasurements();
    }

    sizeAvailability() {
      const map = {};
      ['XS', 'S', 'M', 'L'].forEach((size) => {
        const input = this.findOptionInput(this.activeOptionName, size);
        map[size] = !!(input && !input.classList.contains('disabled') && !input.disabled);
      });
      return map;
    }

    firstAvailableSize() {
      const avail = this.sizeAvailability();
      return ['XS', 'S', 'M', 'L'].find((s) => avail[s]) || null;
    }

    esc(value) {
      return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');
    }

    readSelectedSize(optionName) {
      const root = document.getElementById(`variant-selects-${this.dataset.section}`);
      if (!root) return null;
      const checked = root.querySelector(
        `.product-form__input input[type="radio"][name^="${this.esc(optionName)}-"]:checked`
      );
      return checked ? checked.value : null;
    }

    findOptionInput(optionName, size) {
      const root = document.getElementById(`variant-selects-${this.dataset.section}`);
      if (!root) return null;
      return root.querySelector(
        `.product-form__input input[type="radio"][name^="${this.esc(optionName)}-"][value="${this.esc(size)}"]`
      );
    }

    formatCell(inches) {
      if (this.unit === 'cm') {
        const cm = (inches * 2.54).toFixed(1);
        return { text: cm, aria: `${cm} centimeters` };
      }
      const n = Number(inches);
      const text = `${Number.isInteger(n) ? n : n}"`;
      return { text, aria: `${text} inches` };
    }

    /** Chart highlight only — never changes the live PDP variant. */
    highlightChartSize(size, { announce = true } = {}) {
      const avail = this.sizeAvailability();
      if (!avail[size]) return;
      this.highlightSize = size;
      this.renderMeasurements();
      if (announce && this.statusEl) {
        this.statusEl.textContent = `Showing measurements for size ${size}`;
      }
    }

    renderMeasurements() {
      this.renderMobileSizeRow();
      this.renderMobileValues();
      this.renderDesktopTable();
    }

    renderMobileSizeRow() {
      const avail = this.sizeAvailability();
      this.sizesEl.innerHTML = ['XS', 'S', 'M', 'L']
        .map((size) => {
          const available = avail[size];
          const active = this.highlightSize === size;
          return `<button type="button" class="typh-sf__size${active ? ' is-active' : ''}${available ? '' : ' is-unavailable'}" data-size="${size}" ${available ? '' : 'disabled aria-disabled="true"'} aria-pressed="${active}">${size}</button>`;
        })
        .join('');
      this.sizesEl.querySelectorAll('[data-size]').forEach((btn) => {
        btn.addEventListener('click', () => this.highlightChartSize(btn.getAttribute('data-size')));
      });
    }

    renderMobileValues() {
      const chart = this.chart;
      if (!chart || !this.highlightSize) {
        this.mobileRows.innerHTML = '';
        return;
      }
      const values = chart.rows[this.highlightSize] || [];
      this.mobileRows.innerHTML = chart.columns
        .map((col, i) => {
          const cell = this.formatCell(values[i]);
          return `<div class="typh-sf__mobile-row"><span>${col}</span><span aria-label="${cell.aria}">${cell.text}</span></div>`;
        })
        .join('');
    }

    renderDesktopTable() {
      const chart = this.chart;
      if (!chart) return;
      const sizes = Object.keys(chart.rows);
      const avail = this.sizeAvailability();
      this.thead.innerHTML = `<tr><th scope="col"><span class="visually-hidden">Measurement</span></th>${sizes
        .map((s) => {
          const available = avail[s];
          const active = this.highlightSize === s;
          return `<th scope="col" class="${active ? 'is-active' : ''}${available ? '' : ' is-unavailable'}">
            <button type="button" class="typh-sf__col-btn" data-size="${s}" ${available ? '' : 'disabled'} aria-pressed="${active}">${s}</button>
          </th>`;
        })
        .join('')}</tr>`;
      this.tbody.innerHTML = chart.columns
        .map((col, colIndex) => {
          return `<tr><th scope="row">${col}</th>${sizes
            .map((s) => {
              const cell = this.formatCell(chart.rows[s][colIndex]);
              return `<td class="${this.highlightSize === s ? 'is-active' : ''}${avail[s] ? '' : ' is-unavailable'}" aria-label="${cell.aria}">${cell.text}</td>`;
            })
            .join('')}</tr>`;
        })
        .join('');
      this.thead.querySelectorAll('[data-size]').forEach((btn) => {
        btn.addEventListener('click', () => this.highlightChartSize(btn.getAttribute('data-size')));
      });
    }
  }

  customElements.define('typh-size-fit', TyphSizeFit);
}
